import React, { useState } from 'react';
import { Visit, VisitCancellationReason, CancelVisitRequest } from '../../../dtos/patientmr/visit.dto';

// Import dynamically to avoid circular dependencies
// The actual service call will be injected via onConfirm prop

interface Props {
  visitId: string;
  patientId: string;
  isOpen: boolean;
  onSuccess: (updatedVisit: Visit) => void;
  onClose: () => void;
  onConfirm: (payload: CancelVisitRequest) => Promise<Visit>;
}

const REASON_LABELS: Record<VisitCancellationReason, string> = {
  PATIENT_UNAVAILABLE:   'Patient Unavailable',
  CLINICIAN_UNAVAILABLE: 'Clinician Unavailable',
  WEATHER:               'Weather',
  HOSPITALIZED:          'Hospitalized',
  OTHER:                 'Other (please specify)',
};

export function CancelVisitModal({ visitId, patientId, isOpen, onSuccess, onClose, onConfirm }: Props) {
  const [reason, setReason] = useState<VisitCancellationReason | ''>('');
  const [note, setNote] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!isOpen) return null;

  const isOther = reason === 'OTHER';
  const isNoteRequired = isOther;
  const isNoteEmpty = note.trim() === '';
  const canSubmit = reason !== '' && !(isNoteRequired && isNoteEmpty) && !loading;

  const handleSubmit = async () => {
    if (!reason) return;
    setLoading(true);
    setError(null);
    try {
      const payload: CancelVisitRequest = {
        cancellationReason: reason as VisitCancellationReason,
        ...(note.trim() ? { cancellationNote: note.trim() } : {}),
      };
      const updatedVisit = await onConfirm(payload);
      onSuccess(updatedVisit);
      onClose();
    } catch (err: unknown) {
      const e = err as { statusCode?: number; message?: string };
      if (e?.statusCode === 409) {
        setError('This visit cannot be cancelled because it has already been billed.');
      } else if (e?.statusCode === 404) {
        setError('Visit not found.');
      } else {
        setError('An error occurred. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (loading) return;
    setReason('');
    setNote('');
    setError(null);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" role="dialog" aria-modal="true">
      <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
        <h2 className="text-lg font-semibold text-gray-900 mb-1">Cancel Visit</h2>
        <p className="text-sm text-gray-600 mb-4">This action cannot be undone. Please provide a reason.</p>

        <label className="block text-sm font-medium text-gray-700 mb-1">
          Reason <span className="text-red-500">*</span>
        </label>
        <select
          value={reason}
          onChange={e => { setReason(e.target.value as VisitCancellationReason | ''); setNote(''); setError(null); }}
          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 mb-3"
        >
          <option value="" disabled>Select a reason...</option>
          {(Object.keys(REASON_LABELS) as VisitCancellationReason[]).map(r => (
            <option key={r} value={r}>{REASON_LABELS[r]}</option>
          ))}
        </select>

        {reason && (
          <>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {isOther ? <>Please describe the reason <span className="text-red-500">*</span></> : 'Additional notes (optional)'}
            </label>
            <textarea
              value={note}
              onChange={e => setNote(e.target.value)}
              rows={3}
              placeholder={isOther ? 'Required — describe the reason for cancellation' : 'Optional additional notes'}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </>
        )}

        {error && (
          <p className="mt-2 text-sm text-red-600 bg-red-50 rounded px-3 py-2">{error}</p>
        )}

        <div className="flex gap-3 mt-4 justify-end">
          <button
            onClick={handleClose}
            disabled={loading}
            className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 disabled:opacity-40"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!canSubmit}
            className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {loading && <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />}
            Confirm Cancellation
          </button>
        </div>
      </div>
    </div>
  );
}
